import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

// The pages that aren't on the default paper: white for reading, and the
// homepage, which fades between the two (see Home.js).
const PAGE_TONE = {
  '/': 'site--home',
  '/faq': 'site--white',
  '/pricing': 'site--white',
};

function Layout() {
  const { t } = useTranslation();
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  // A link opens its page at the top, not wherever the last page was scrolled
  // to (the router keeps the scroll position). Back and forward keep the
  // browser's own memory, and a link to a section (/faq#delivery) goes there.
  // Instant: the site's smooth scrolling would glide the whole way up.
  useLayoutEffect(() => {
    if (navigationType === 'POP' || hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, navigationType]);

  // On a phone the links don't fit in the header, so they live behind a
  // menu button: a full page of paper with the links large. It closes on
  // any navigation and on Escape, and the page behind doesn't scroll.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname, hash]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', onKey);
    document.documentElement.classList.add('menu-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('menu-open');
    };
  }, [menuOpen]);

  return (
    <div className={`site site--atelier atelier ${PAGE_TONE[pathname] ?? ''}`}>
      <header className="nav">
        <Link to="/" className="nav-brand">
          <img className="brand-logo" src="/logo.svg" alt="myMosaik" />
        </Link>
        <nav className="nav-links">
          <Link to="/how-it-works">{t('nav.howItWorks')}</Link>
          <Link to="/pricing">{t('nav.pricing')}</Link>
          <Link to="/faq">{t('nav.faq')}</Link>
        </nav>
        <LanguageSwitcher />
        <Link to="/order" className="button button-primary nav-cta">{t('nav.start')}</Link>
        <button
          type="button"
          className={`nav-menu ${menuOpen ? 'is-open' : ''}`}
          aria-expanded={menuOpen}
          aria-controls="nav-panel"
          aria-label={menuOpen ? t('nav.close') : t('nav.menu')}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="nav-menu-lines" aria-hidden="true" />
        </button>
      </header>

      <div id="nav-panel" className={`nav-panel ${menuOpen ? 'is-open' : ''}`} hidden={!menuOpen}>
        <nav className="nav-panel-links" onClick={() => setMenuOpen(false)}>
          <Link to="/how-it-works">{t('nav.howItWorks')}</Link>
          <Link to="/pricing">{t('nav.pricing')}</Link>
          <Link to="/faq">{t('nav.faq')}</Link>
          <Link to="/contact">{t('nav.contact')}</Link>
        </nav>
        <LanguageSwitcher />
      </div>

      <main>
        <Outlet />
      </main>

      <footer className="footer">
        <span>&copy; {new Date().getFullYear()} myMosaik</span>
        <div className="footer-links">
          <Link to="/contact">{t('nav.contact')}</Link>
          <Link to="/organisations">{t('nav.organisations')}</Link>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
